/**
 * Componente para mostrar carrusel de imágenes de un álbum.
 * Adaptado para la web usando react-responsive-carousel.
 */

import React, { useState } from 'react';
import { FiX } from 'react-icons/fi';
import Rodal from 'rodal';
import 'rodal/lib/rodal.css';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

/**
 * Componente ImageCarousel
 * 
 * Muestra un carrusel de imágenes con navegación y contador.
 * 
 * @param {boolean} visible - Indica si el carrusel debe ser visible
 * @param {Function} onClose - Callback al cerrar el carrusel
 * @param {Array} images - Lista de imágenes a mostrar
 * @param {number} initialIndex - Índice inicial del carrusel
 */
export default function ImageCarousel({ visible, onClose, images, initialIndex = 0 }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  if (!visible || !images || images.length === 0) {
    return null;
  }

  return (
    <Rodal 
      visible={visible} 
      onClose={onClose}
      animation="fade"
      closeOnEsc={true}
      closeMaskOnClick={true}
      showCloseButton={false}
      showMask={false}
      customStyles={{
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(100px)', 
        WebkitBackdropFilter: 'blur(100px)',
        padding: '0',
        width: '100%',
        height: '100%',
      }}
    >
      <div className="flex flex-col h-full">
        {/* Header con botón de cerrar y contador */}
        <div className="flex justify-between items-center p-4 bg-neutral-900 z-10">
          <div className="w-10" />
          <div className="flex items-center gap-1">
            <span className="text-white text-base font-medium">{currentIndex + 1}</span>
            <span className="text-white text-base font-medium">de</span>
            <span className="text-white text-base font-medium">{images.length}</span>
          </div>
          <button 
            onClick={onClose} 
            className="text-white hover:text-gray-300 transition-colors"
          >
            <FiX size={28} />
          </button>
        </div>

        {/* Carrusel de imágenes */}
        <div className="flex-1 flex items-center justify-center">
          <Carousel
            selectedItem={currentIndex}
            onChange={setCurrentIndex}
            showThumbs={false}
            showStatus={false}
            showIndicators={true}
            infiniteLoop={true}
            useKeyboardArrows={true}
            renderArrowPrev={(onClickHandler, hasPrev, label) =>
              hasPrev && (
                <button
                  type="button"
                  onClick={onClickHandler}
                  title={label}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-4 text-white bg-neutral-700/50 hover:bg-neutral-900/70 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )
            }
            renderArrowNext={(onClickHandler, hasNext, label) =>
              hasNext && (
                <button
                  type="button"
                  onClick={onClickHandler}
                  title={label}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-4 text-white bg-neutral-700/50 hover:bg-neutral-700/70 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )
            }
          >
            {images?.map((item, index) => (
              <div key={index} className="flex items-center justify-center h-full">
                <img
                  src={item.url}
                  alt={`Imagen ${index + 1}`}
                  className="max-h-[80vh] max-w-[80vw] object-contain"
                />
              </div>
            ))}
          </Carousel>
        </div>
      </div>
    </Rodal>
  );
}
